// ════════════════════════════════════════════════════════════════════════════
// M2.7 — banco de preguntas PEMM-Proceso (cuestionario · 0745)
// "Banco ampliado" (decisión CEO): 3 preguntas por habilitador × 13 = 39, escala
// 1-4. Cada pregunta es una AFIRMACIÓN DE MADUREZ derivada de los descriptores
// canónicos de Hammer (descriptores-pemm-proceso.ts): las 3 preguntas de un
// habilitador escalan P-2 → P-3 → P-4, de modo que la MEDIA de sus respuestas
// (1-4) rueda al nivel del habilitador (pPorMedia en el scoring del backend) y el
// global = eslabón débil (MIN). Espejo conceptual del cuestionario BPMM (35 preg).
//
// clave = `${habilitador}_qN` → el backend deduce el habilitador parseando la clave.
// ════════════════════════════════════════════════════════════════════════════
import type { CategoriaPEMM } from './descriptores-pemm-proceso';

export interface PreguntaPemmProceso {
  /** `${habilitadorClave}_qN` — única; el backend parte por el último `_q`. */
  clave: string;
  /** clave del habilitador al que agrega (HABILITADORES_PEMM). */
  habilitador: string;
  categoria: CategoriaPEMM;
  texto: string;
}

/** Escala 1-4 del cuestionario PEMM (grado de cumplimiento de la afirmación). */
export interface OpcionEscalaPemm {
  valor: 1 | 2 | 3 | 4;
  etiqueta: string;
}
export const ESCALA_PEMM: OpcionEscalaPemm[] = [
  { valor: 1, etiqueta: 'Nada' },
  { valor: 2, etiqueta: 'Parcialmente' },
  { valor: 3, etiqueta: 'En gran medida' },
  { valor: 4, etiqueta: 'Plenamente' },
];

/** 39 preguntas (3 por habilitador). Orden = orden canónico de HABILITADORES_PEMM. */
export const PREGUNTAS_PEMM_PROCESO: PreguntaPemmProceso[] = [
  // ── DISEÑO ────────────────────────────────────────────────────────────────
  {
    clave: 'proposito_q1',
    habilitador: 'proposito',
    categoria: 'DISENO',
    texto:
      'El proceso está diseñado de extremo a extremo para optimizar el resultado global, no funciones o silos por separado.',
  },
  {
    clave: 'proposito_q2',
    habilitador: 'proposito',
    categoria: 'DISENO',
    texto: 'El diseño del proceso encaja con la estrategia y con otros procesos de la empresa.',
  },
  {
    clave: 'proposito_q3',
    habilitador: 'proposito',
    categoria: 'DISENO',
    texto:
      'El diseño se extiende e integra con los procesos de clientes y proveedores (sistema de valor).',
  },
  {
    clave: 'contexto_q1',
    habilitador: 'contexto',
    categoria: 'DISENO',
    texto:
      'Están claramente identificados las entradas, salidas, proveedores y clientes del proceso.',
  },
  {
    clave: 'contexto_q2',
    habilitador: 'contexto',
    categoria: 'DISENO',
    texto:
      'Las necesidades de los clientes están acordadas y el propietario pacta métricas e interfaces con otros procesos.',
  },
  {
    clave: 'contexto_q3',
    habilitador: 'contexto',
    categoria: 'DISENO',
    texto:
      'Las expectativas del proceso se fijan conjuntamente con clientes y proveedores del sistema de valor.',
  },
  {
    clave: 'documentacion_q1',
    habilitador: 'documentacion',
    categoria: 'DISENO',
    texto: 'Existe documentación del proceso de principio a fin (no solo funcional o por áreas).',
  },
  {
    clave: 'documentacion_q2',
    habilitador: 'documentacion',
    categoria: 'DISENO',
    texto:
      'La documentación describe las interfaces con otros procesos y enlaza con la arquitectura de datos y sistemas.',
  },
  {
    clave: 'documentacion_q3',
    habilitador: 'documentacion',
    categoria: 'DISENO',
    texto:
      'La documentación es electrónica y dinámica, y soporta la ejecución y gestión del proceso.',
  },
  // ── EJECUTANTES ───────────────────────────────────────────────────────────
  {
    clave: 'conocimiento_q1',
    habilitador: 'conocimiento',
    categoria: 'EJECUTANTES',
    texto:
      'Los ejecutantes saben nombrar el proceso y sus métricas, y describen su flujo global de extremo a extremo.',
  },
  {
    clave: 'conocimiento_q2',
    habilitador: 'conocimiento',
    categoria: 'EJECUTANTES',
    texto:
      'Los ejecutantes conocen conceptos de negocio y cómo su trabajo afecta al rendimiento de la empresa.',
  },
  {
    clave: 'conocimiento_q3',
    habilitador: 'conocimiento',
    categoria: 'EJECUTANTES',
    texto:
      'Los ejecutantes conocen tendencias del sector y el impacto de su trabajo a nivel inter-empresa.',
  },
  {
    clave: 'habilidades_q1',
    habilitador: 'habilidades',
    categoria: 'EJECUTANTES',
    texto: 'Los ejecutantes tienen habilidades de resolución de problemas y mejora del proceso.',
  },
  {
    clave: 'habilidades_q2',
    habilitador: 'habilidades',
    categoria: 'EJECUTANTES',
    texto: 'Los ejecutantes trabajan en equipo, se autogestionan y toman decisiones de negocio.',
  },
  {
    clave: 'habilidades_q3',
    habilitador: 'habilidades',
    categoria: 'EJECUTANTES',
    texto: 'Los ejecutantes gestionan e implementan el cambio del proceso.',
  },
  {
    clave: 'comportamiento_q1',
    habilitador: 'comportamiento',
    categoria: 'EJECUTANTES',
    texto:
      'Los ejecutantes muestran lealtad al proceso (más allá de su función) y siguen el diseño correctamente.',
  },
  {
    clave: 'comportamiento_q2',
    habilitador: 'comportamiento',
    categoria: 'EJECUTANTES',
    texto:
      'Los ejecutantes se esfuerzan por que el proceso entregue resultados para los objetivos de la empresa.',
  },
  {
    clave: 'comportamiento_q3',
    habilitador: 'comportamiento',
    categoria: 'EJECUTANTES',
    texto: 'Los ejecutantes buscan señales de que el proceso debe cambiar y proponen mejoras.',
  },
  // ── PROPIETARIO ───────────────────────────────────────────────────────────
  {
    clave: 'identidad_q1',
    habilitador: 'identidad',
    categoria: 'PROPIETARIO',
    texto:
      'Existe un rol oficial de propietario del proceso, ocupado por un directivo con peso y credibilidad.',
  },
  {
    clave: 'identidad_q2',
    habilitador: 'identidad',
    categoria: 'PROPIETARIO',
    texto: 'El propietario forma parte del órgano de decisión más alto de la empresa.',
  },
  {
    clave: 'identidad_q3',
    habilitador: 'identidad',
    categoria: 'PROPIETARIO',
    texto:
      'El propietario está en el máximo órgano de gobierno y puede liderar un comité de procesos.',
  },
  {
    clave: 'actividades_q1',
    habilitador: 'actividades',
    categoria: 'PROPIETARIO',
    texto:
      'El propietario comunica objetivos, patrocina el rediseño/mejora y planifica la implantación.',
  },
  {
    clave: 'actividades_q2',
    habilitador: 'actividades',
    categoria: 'PROPIETARIO',
    texto:
      'El propietario trabaja con otros propietarios para integrar procesos hacia los objetivos de empresa.',
  },
  {
    clave: 'actividades_q3',
    habilitador: 'actividades',
    categoria: 'PROPIETARIO',
    texto:
      'El propietario desarrolla el plan estratégico del proceso y colabora con clientes y proveedores.',
  },
  {
    clave: 'autoridad_q1',
    habilitador: 'autoridad',
    categoria: 'PROPIETARIO',
    texto:
      'El propietario controla el diseño del proceso e influye en su presupuesto y en el personal.',
  },
  {
    clave: 'autoridad_q2',
    habilitador: 'autoridad',
    categoria: 'PROPIETARIO',
    texto: 'El propietario controla el presupuesto de TI y los proyectos que cambian el proceso.',
  },
  {
    clave: 'autoridad_q3',
    habilitador: 'autoridad',
    categoria: 'PROPIETARIO',
    texto:
      'El propietario controla el presupuesto del proceso y tiene fuerte influencia sobre el personal.',
  },
  // ── INFRAESTRUCTURA ───────────────────────────────────────────────────────
  {
    clave: 'sistemas_info_q1',
    habilitador: 'sistemas_info',
    categoria: 'INFRAESTRUCTURA',
    texto:
      'Un sistema construido a partir de componentes soporta el proceso (más allá de sistemas heredados y fragmentados).',
  },
  {
    clave: 'sistemas_info_q2',
    habilitador: 'sistemas_info',
    categoria: 'INFRAESTRUCTURA',
    texto:
      'Un sistema integrado, diseñado para el proceso y conforme a estándares de empresa, lo soporta.',
  },
  {
    clave: 'sistemas_info_q3',
    habilitador: 'sistemas_info',
    categoria: 'INFRAESTRUCTURA',
    texto: 'Un sistema modular con estándares del sector soporta la comunicación inter-empresa.',
  },
  {
    clave: 'sistemas_rrhh_q1',
    habilitador: 'sistemas_rrhh',
    categoria: 'INFRAESTRUCTURA',
    texto:
      'El diseño del proceso define roles, descripciones de puesto, perfiles de competencia y formación.',
  },
  {
    clave: 'sistemas_rrhh_q2',
    habilitador: 'sistemas_rrhh',
    categoria: 'INFRAESTRUCTURA',
    texto:
      'La contratación, el desarrollo y las recompensas enfatizan las necesidades y resultados del proceso.',
  },
  {
    clave: 'sistemas_rrhh_q3',
    habilitador: 'sistemas_rrhh',
    categoria: 'INFRAESTRUCTURA',
    texto: 'Los sistemas de RR.HH. refuerzan la colaboración intra e inter-empresa.',
  },
  // ── MÉTRICAS ──────────────────────────────────────────────────────────────
  {
    clave: 'definicion_q1',
    habilitador: 'definicion',
    categoria: 'METRICAS',
    texto:
      'El proceso tiene métricas de extremo a extremo derivadas de los requisitos del cliente.',
  },
  {
    clave: 'definicion_q2',
    habilitador: 'definicion',
    categoria: 'METRICAS',
    texto: 'Las métricas del proceso derivan de los objetivos estratégicos de la empresa.',
  },
  {
    clave: 'definicion_q3',
    habilitador: 'definicion',
    categoria: 'METRICAS',
    texto:
      'Las métricas del proceso derivan de objetivos inter-empresa (con clientes y proveedores).',
  },
  {
    clave: 'usos_q1',
    habilitador: 'usos',
    categoria: 'METRICAS',
    texto:
      'Las métricas se usan para comparar con benchmarks y necesidades del cliente y para fijar metas.',
  },
  {
    clave: 'usos_q2',
    habilitador: 'usos',
    categoria: 'METRICAS',
    texto:
      'Las métricas se presentan a los ejecutantes y se usan en la gestión diaria del proceso.',
  },
  {
    clave: 'usos_q3',
    habilitador: 'usos',
    categoria: 'METRICAS',
    texto: 'Las métricas se revisan periódicamente y se usan en la planificación estratégica.',
  },
];

/** Preguntas de un habilitador (en orden). */
export function preguntasDeHabilitador(habilitadorClave: string): PreguntaPemmProceso[] {
  return PREGUNTAS_PEMM_PROCESO.filter((p) => p.habilitador === habilitadorClave);
}

/** Deriva la clave del habilitador desde la clave de pregunta (`${hab}_qN`). */
export function habilitadorDePregunta(preguntaClave: string): string {
  const i = preguntaClave.lastIndexOf('_q');
  return i > 0 ? preguntaClave.slice(0, i) : preguntaClave;
}

/** Forma persistida en `EvaluacionPEMMProceso.respuestas` (jsonb). */
export type RespuestasPemm = Record<string, { valor: number; nota?: string }>;
